import { Paper, Select, Stack, Text, useMantineTheme } from '@mantine/core';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash/fp';
import React, { useMemo } from 'react';
import {
  Bar,
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { DeviceStatusHistoryType } from '@portals/types';

import { DeviceStatusBar } from './DeviceStatusBar';
import {
  DeviceStatusTimelineType,
  TIMELINE_RANGE_OPTIONS,
  TIMELINE_RANGE_OPTIONS_NUMERIC_VALUES,
  TimelineRangeNamesType,
} from '../types/device-status-incident-timeline-widget.types';

export interface DeviceStatusIncidentTimelineWidgetProps {
  name: string;
  deviceStateHistory: DeviceStatusHistoryType[];
  onChangeSelectedTimeRange: (time: TimelineRangeNamesType) => void;
  selectedTimeRange: TimelineRangeNamesType;
}

const TIME_THRESHOLD = TIMELINE_RANGE_OPTIONS_NUMERIC_VALUES.oneDay;
const INITIAL_Y_VALUE = 0.6;

export function DeviceStatusIncidentTimelineWidget({
  name,
  deviceStateHistory,
  selectedTimeRange,
  onChangeSelectedTimeRange,
}: DeviceStatusIncidentTimelineWidgetProps) {
  const theme = useMantineTheme();

  const statusHistory: DeviceStatusTimelineType[] = useMemo(() => {
    if (deviceStateHistory.length === 0) {
      return [];
    }

    return deviceStateHistory.map((statusHistory) => ({
      status: statusHistory.status,
      meta: {
        actualStart: new Date(statusHistory.meta.actual_start),
        actualEnd: new Date(statusHistory.meta.actual_end),
      },
      start: new Date(statusHistory.start),
      end: new Date(statusHistory.end),
      name: 'Device Status',
      y: INITIAL_Y_VALUE,
    }));
  }, [deviceStateHistory]);

  const xLastEntry = statusHistory[statusHistory.length - 1]?.end?.getTime();

  const xFirstEntry = statusHistory[0]?.start?.getTime();

  return (
    <Paper p="xxl" radius="lg" shadow="xl">
      <Stack align="flex-start" w="fit-content">
        <Text>{name}</Text>

        <Select
          data={[
            { value: TIMELINE_RANGE_OPTIONS.oneHour, label: 'Last 1 hour' },
            { value: TIMELINE_RANGE_OPTIONS.sixHours, label: 'Last 6 hours' },
            { value: TIMELINE_RANGE_OPTIONS.oneDay, label: 'Last 1 day' },
            { value: TIMELINE_RANGE_OPTIONS.sevenDays, label: 'Last 7 days' },
          ]}
          value={selectedTimeRange}
          onChange={onChangeSelectedTimeRange}
        />
      </Stack>

      {isEmpty(statusHistory) ? (
        <Stack align="center" justify="center" w="100%" h="100%">
          <Text size="sm" color="gray.5">
            No data
          </Text>
        </Stack>
      ) : (
        <ResponsiveContainer minWidth={400} width="100%" height={65}>
          <ComposedChart
            data={statusHistory}
            layout="vertical"
            margin={{ top: 15, right: 30, left: 50 }}
          >
            <XAxis
              type="number"
              dataKey="start"
              domain={[xFirstEntry, xLastEntry]}
              scale="time"
              tickFormatter={(value) =>
                TIMELINE_RANGE_OPTIONS_NUMERIC_VALUES[selectedTimeRange] >
                TIME_THRESHOLD
                  ? dayjs(value).tz('UTC').format('MMM DD')
                  : dayjs(value).tz('UTC').format('HH:mm')
              }
              axisLine={false}
              tickCount={10}
              tick={{ fill: theme.colors.gray[5] }}
            />

            <YAxis
              type="number"
              dataKey="y"
              domain={[INITIAL_Y_VALUE, INITIAL_Y_VALUE]}
              ticks={[INITIAL_Y_VALUE]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: theme.fn.primaryColor(), width: 100 }}
              tickFormatter={() => 'Device Status'}
            />

            <Bar
              dataKey={({ start, end }) => [start.getTime(), end.getTime()]}
              barSize={12}
              shape={(props) => <DeviceStatusBar {...props} />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}
